import { contractConfig } from "@config/contractsConfig";
import {
  // getElMerkleProof,
  getElMerkleProofList,
  getKernelS3V1MerkleProofListBsc,
  getKernelS3V1MerkleProofListEth,
  getScrollMerkleProofList,
} from "@services/index";
import { wagmiConfig } from "@services/WagmiProvider";
import { useQuery } from "@tanstack/react-query";
import { readContracts } from "@wagmi/core";
import AirdropsMetadata from "@data/airdrops.json";

import { Abi, Hash } from "viem";
import { bsc, mainnet, scroll } from "viem/chains";
import { useAccount } from "wagmi";

import {
  getUTCEquivalentDate,
  isAmountClaimedOrAvailable,
} from "@common/utils";
import kernelClaimAirdropAssets from "@data/kernelClaimAirdropAssets.json";

export enum ClaimType {
  SCROLL = "scroll",
  // EL = "el",
  ELPK = "elPk",
  KERNEL_S3V1 = "kernelS3V1",
  KERNEL_S3V1_BSC = "kernelS3V1Bsc",
}

type ClaimConfig = {
  contract: { address: Hash; abi: Abi };
  asset: string;
  unit: string;
  chainId: number;
  fetchMerkleProofList: (address: Hash) => Promise<any>;
  getTotalAmount: (merkleProofList: any) => bigint;
  getProofs: (merkleProofList: any) => any;
  iconPath: string;
  completeVestingDate?: string;
  claimEndDate?: string;
  launchDate?: string;
  tooltipContent?: string;
};

export type Airdrop = {
  claimedAmount: bigint;
  totalAmount: bigint;
  unit: string;
  availableAmount: bigint;
  currentIndex: bigint;
  asset: string;
  chainId: number;
  type: ClaimType;
  contract: { address: Hash; abi: Abi };
  proofs: any;
  iconPath: string;
  lastUpdatedOn?: string;
  completeVestingDate?: string;
  claimEndDate?: string;
  availableAmountToClaim?: bigint;
  launchDate?: string;
  tooltipContent?: string;
};

export type ClaimData = {
  data: Airdrop;
  isLoading: boolean;
  key: ClaimType;
  refetch: () => void;
  isRefetching: boolean;
  error: any;
};

type ClaimConfigs = {
  [key in ClaimType]: ClaimConfig;
};

const configs: ClaimConfigs = {
  [ClaimType.SCROLL]: {
    contract: contractConfig[scroll.id].interactionPools.scrollDistributor,
    chainId: scroll.id,
    asset: "Scroll Token",
    unit: "SCR",
    fetchMerkleProofList: getScrollMerkleProofList,
    getTotalAmount: (merkleProofList) =>
      BigInt(merkleProofList?.data?.marks || 0),
    getProofs: (merkleProofList) => merkleProofList?.data?.proof,
    iconPath: "/assets/chains/scroll.svg",
    claimEndDate: "28 Jan 2025", // TODO: To be moved to CMS
  },
  // [ClaimType.EL]: {
  //   contract: contractConfig[mainnet.id].interactionPools.elMerkleDistributor,
  //   chainId: mainnet.id,
  //   unit: "EIGEN",
  //   asset: "Eigen - Season 2",
  //   fetchMerkleProofList: getElMerkleProofList,
  //   getTotalAmount: (merkleProofList) => BigInt(merkleProofList?.data?.el || 0),
  //   getProofs: (merkleProofList) => merkleProofList?.data?.proofs,
  //   iconPath: "/assets/claim/eigen.svg",
  // },
  [ClaimType.ELPK]: {
    contract: contractConfig[mainnet.id].interactionPools.elPkMerkleDistributor,
    chainId: mainnet.id,
    unit: "EIGEN",
    asset: "Eigen - Programmatic Rewards",
    fetchMerkleProofList: getElMerkleProofList,
    getTotalAmount: (merkleProofList) =>
      BigInt(merkleProofList?.programmatic?.el || 1),
    getProofs: (merkleProofList) => merkleProofList?.programmatic?.proofs,
    iconPath: "/assets/claim/eigen.svg",
  },
  [ClaimType.KERNEL_S3V1]: {
    contract:
      contractConfig[mainnet.id].interactionPools
        .kernelAirdrop_S3V1_MerkleDistributorEth,
    chainId: mainnet.id,
    unit: "$KERNEL",
    asset: "$KERNEL Season 3 Vesting",
    fetchMerkleProofList: getKernelS3V1MerkleProofListEth,
    getTotalAmount: (merkleProofList) => {
      return BigInt(merkleProofList?.data?.balance || 1);
    },
    getProofs: (merkleProofList) => {
      return merkleProofList?.data?.proof;
    },
    iconPath: "/assets/claim/kernel_32.png",
    completeVestingDate:
      kernelClaimAirdropAssets?.airdropSchedules?.Season3_Vesting1
        ?.vestingEndDate,
    claimEndDate:
      kernelClaimAirdropAssets?.airdropSchedules?.Season3_Vesting1
        ?.claimEndDate,
    launchDate:
      kernelClaimAirdropAssets?.airdropSchedules?.Season3_Vesting1?.launchDate,
    tooltipContent:
      "$KERNEL Season 3 allocation will vest over 30 days. Vesting starts on 26th Oct'25 and the entire allocation can be claimed by 25th Nov'25.",
  },
  [ClaimType.KERNEL_S3V1_BSC]: {
    contract:
      contractConfig[bsc.id].interactionPools
        .kernelAirdrop_S3V1_MerkleDistributorBsc,
    chainId: bsc.id,
    unit: "$KERNEL",
    asset: "$KERNEL Season 3 Vesting",
    fetchMerkleProofList: getKernelS3V1MerkleProofListBsc,
    getTotalAmount: (merkleProofList) => {
      return BigInt(merkleProofList?.data?.balance || 1);
    },
    getProofs: (merkleProofList) => {
      return merkleProofList?.data?.proof;
    },
    iconPath: "/assets/claim/kernel_32.png",
    completeVestingDate:
      kernelClaimAirdropAssets?.airdropSchedules?.Season3_Vesting1
        ?.vestingEndDate,
    claimEndDate:
      kernelClaimAirdropAssets?.airdropSchedules?.Season3_Vesting1
        ?.claimEndDate,
    launchDate:
      kernelClaimAirdropAssets?.airdropSchedules?.Season3_Vesting1?.launchDate,
    tooltipContent:
      "$KERNEL Season 3 allocation will vest over 30 days. Vesting starts on 26th Oct'25 and the entire allocation can be claimed by 25th Nov'25.",
  },
};

const fetchMerkleData = async (
  config: ClaimConfig,
  address: Hash,
  type: ClaimType
) => {
  const [
    { result: currentIndex },
    { result: userClaims },
    // { result: currentMerkleRoot },
  ] = await readContracts(wagmiConfig, {
    contracts: [
      {
        ...config.contract,
        functionName: "currentIndex",
        chainId: config.chainId,
      },
      {
        ...config.contract,
        functionName: "userClaims",
        args: [address],
        chainId: config.chainId,
      },
      //   {
      //     ...config.contract,
      //     functionName: "currentMerkleRoot",
      //     args: [],
      //     chainId: config.chainId,
      //   },
    ],
  });

  const { data: merkleProofList } = await config.fetchMerkleProofList(address);

  const totalAmount = config.getTotalAmount(merkleProofList);
  const claimedAmount = (userClaims as [bigint, bigint])[1] || 0n;
  const availableAmount = totalAmount - claimedAmount;

  const meta: Record<string, any> = AirdropsMetadata?.metadata;
  const lastUpdatedOn: string = meta?.[type]?.last_updated_on || "";

  return {
    claimedAmount,
    totalAmount,
    deadline: "01 Dec 2024",
    lastUpdatedOn: lastUpdatedOn,
    unit: config.unit,
    availableAmount,
    availableAmountToClaim: availableAmount,
    currentIndex: currentIndex as bigint,
    asset: config.asset,
    chainId: config.chainId,
    type,
    contract: config.contract,
    proofs: config.getProofs(merkleProofList),
    iconPath: config.iconPath,
    completeVestingDate: config.completeVestingDate,
    claimEndDate: config.claimEndDate,
    launchDate: config.launchDate ?? "",
    tooltipContent: config.tooltipContent,
  };
};

const useFetchMerkleData = (type: ClaimType, address?: Hash) => {
  return useQuery({
    queryKey: ["claim", type, address],
    queryFn: async () => fetchMerkleData(configs[type], address as Hash, type),
    enabled: !!address,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

const fetchClaimableAmountData = async (
  config: ClaimConfig,
  address: Hash,
  amount?: bigint
) => {
  const [{ result: claimableAmount }] = await readContracts(wagmiConfig, {
    contracts: [
      {
        ...config.contract,
        functionName: "getClaimableAmount",
        args: [address, amount],
        chainId: config.chainId,
      },
    ],
  });

  const availableAmountToClaim = claimableAmount as bigint;
  return availableAmountToClaim;
};

const useFetchClaimableAmountData = (
  type: ClaimType,
  address?: Hash,
  amount?: bigint
) => {
  return useQuery({
    queryKey: ["ClaimableAmountData", type, address, amount?.toString()],
    queryFn: async () =>
      fetchClaimableAmountData(configs[type], address as Hash, amount),
    enabled: !!address && !!amount,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

const useClaims = () => {
  const { address } = useAccount();
  // update address here for local testing

  // const {
  //   data: elData,
  //   isLoading: elDataLoading,
  //   error: elError,
  //   refetch: refetchEl,
  //   isRefetching: elRefetching,
  // } = useFetchMerkleData(ClaimType.EL, address);

  const {
    data: kernelDataS3V1,
    isLoading: isKernelLoadingS3V1,
    error: kernelErrorS3V1,
    refetch: refetchKernelS3V1,
    isRefetching: kernelRefetchingS3V1,
  } = useFetchMerkleData(ClaimType.KERNEL_S3V1, address);

  const {
    data: kernelDataS3V1Bsc,
    isLoading: isKernelLoadingS3V1Bsc,
    error: kernelErrorS3V1Bsc,
    refetch: refetchKernelS3V1Bsc,
    isRefetching: kernelRefetchingS3V1Bsc,
  } = useFetchMerkleData(ClaimType.KERNEL_S3V1_BSC, address);

  const {
    data: scrollData,
    isLoading: scrollDataLoading,
    error: scrollError,
    refetch: refetchScroll,
    isRefetching: scrollRefetching,
  } = useFetchMerkleData(ClaimType.SCROLL, address);

  const {
    data: kernelAvailableAmountS3V1,
    refetch: refetchKernelClaimableAmountS3V1,
  } = useFetchClaimableAmountData(
    ClaimType.KERNEL_S3V1,
    address,
    kernelDataS3V1?.totalAmount
  );

  const {
    data: kernelAvailableAmountS3V1Bsc,
    refetch: refetchKernelClaimableAmountS3V1Bsc,
  } = useFetchClaimableAmountData(
    ClaimType.KERNEL_S3V1_BSC,
    address,
    kernelDataS3V1Bsc?.totalAmount
  );

  const kernelAvailableDataS3V1 = {
    ...kernelDataS3V1,
    availableAmountToClaim: kernelAvailableAmountS3V1,
  };

  // FOR TESTING ONLY - Force zero amount for S2V1 ETH
  // const kernelAvailableDataS2V1 = {
  //   ...kernelDataS2V1,
  //   availableAmountToClaim: 0n,
  //   availableAmount: 0n,
  // };

  const kernelAvailableDataS3V1Bsc = {
    ...kernelDataS3V1Bsc,
    availableAmountToClaim: kernelAvailableAmountS3V1Bsc,
  };

  const {
    data: elpkData,
    isLoading: elpkDataLoading,
    error: elpkError,
    refetch: refetchElPk,
    isRefetching: elpkRefetching,
  } = useFetchMerkleData(ClaimType.ELPK, address);

  if (elpkError) {
    console.log({ elpkError });
  }

  const isLoading =
    elpkDataLoading ||
    scrollDataLoading ||
    isKernelLoadingS3V1 ||
    isKernelLoadingS3V1Bsc;

  const data = [
    // {
    //   isLoading: elDataLoading,
    //   data: elData,
    //   key: ClaimType.EL,
    //   refetch: refetchEl,
    //   isRefetching: elRefetching,
    //   error: elError,
    // },
    {
      isLoading: elpkDataLoading,
      data: elpkData,
      key: ClaimType.ELPK,
      refetch: refetchElPk,
      isRefetching: elpkRefetching,
      error: elpkError,
    },
    {
      isLoading: isKernelLoadingS3V1Bsc,
      data: isAmountClaimedOrAvailable(
        kernelAvailableAmountS3V1Bsc,
        kernelAvailableDataS3V1Bsc?.claimedAmount
      )
        ? kernelAvailableDataS3V1Bsc
        : null,
      key: ClaimType.KERNEL_S3V1_BSC,
      refetch: () => {
        refetchKernelS3V1Bsc();
        refetchKernelClaimableAmountS3V1Bsc();
      },
      isRefetching: kernelRefetchingS3V1Bsc,
      error: kernelErrorS3V1Bsc,
    },
    {
      isLoading: isKernelLoadingS3V1,
      data: isAmountClaimedOrAvailable(
        kernelAvailableAmountS3V1,
        kernelAvailableDataS3V1?.claimedAmount
      )
        ? kernelAvailableDataS3V1
        : null,
      key: ClaimType.KERNEL_S3V1,
      refetch: () => {
        refetchKernelS3V1();
        refetchKernelClaimableAmountS3V1();
      },
      isRefetching: kernelRefetchingS3V1,
      error: kernelErrorS3V1,
    },
    {
      isLoading: scrollDataLoading,
      data: scrollData,
      key: ClaimType.SCROLL,
      refetch: refetchScroll,
      isRefetching: scrollRefetching,
      error: scrollError,
    },
  ].filter(
    (d) =>
      !d.error &&
      d.data &&
      (!d.data.launchDate ||
        (d.data.launchDate &&
          new Date(Date.now()).toISOString() >=
            getUTCEquivalentDate(d.data.launchDate).toISOString()))
  );
  return { isLoading, data };
};

export default useClaims;
